import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOffIcon, Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { passwordSchema, resetPasswordSchema } from '@shared/schemas/auth';
import { authClient, resetPassword, setInitialPassword } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const setPasswordFormSchema = resetPasswordSchema.extend({
  confirmPassword: passwordSchema,
}).refine((values: { confirmPassword: string; newPassword: string }) => values.newPassword === values.confirmPassword, {
  error: 'Les mots de passe doivent être identiques.',
  path: ['confirmPassword'],
});

type SetPasswordFormValues = z.infer<typeof setPasswordFormSchema>;

export function SetPasswordForm({
  className,
  token = null,
  ...props
}: React.ComponentProps<'form'> & { token?: string | null }) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const form = useForm<SetPasswordFormValues>({
    defaultValues: {
      confirmPassword: '',
      newPassword: '',
    },
    resolver: zodResolver(setPasswordFormSchema),
  });

  async function handleSubmit(values: SetPasswordFormValues) {
    form.clearErrors('root');

    const result = token
      ? await resetPassword(values.newPassword, token)
      : await setInitialPassword(values.newPassword);

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    if (token) {
      toast.success('Mot de passe défini. Tu peux maintenant te connecter.');
      navigate('/auth/login', { replace: true });
      return;
    }

    await authClient.getSession();
    toast.success('Mot de passe défini. Session active.');
    window.location.replace('/');
  }

  return (
    <form className={cn('flex flex-col gap-6', className)} onSubmit={form.handleSubmit(handleSubmit)} {...props}>
      <FieldGroup>
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-bold">Choisis ton mot de passe</h1>
          <p className="text-sm text-balance text-muted-foreground">
            Ton email est confirmé. Il ne reste qu'à sécuriser le compte avant d'entrer dans l'application.
          </p>
        </div>
        <Field>
          <FieldLabel htmlFor="set-password">Mot de passe <span className="text-destructive">*</span></FieldLabel>
          <div className="relative">
            <Input
              id="set-password"
              data-testid="set-password-input"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Saisissez votre mot de passe"
              className="bg-background pr-12"
              aria-invalid={form.formState.errors.newPassword ? 'true' : 'false'}
              {...form.register('newPassword')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
            </button>
          </div>
          <FieldDescription>Au moins 8 caractères.</FieldDescription>
          <FieldError errors={[form.formState.errors.newPassword]} />
        </Field>
        <Field>
          <FieldLabel htmlFor="set-password-confirmation">Confirmation du mot de passe <span className="text-destructive">*</span></FieldLabel>
          <div className="relative">
            <Input
              id="set-password-confirmation"
              data-testid="set-password-confirmation-input"
              type={showConfirmation ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Confirmez votre mot de passe"
              className="bg-background pr-12"
              aria-invalid={form.formState.errors.confirmPassword ? 'true' : 'false'}
              {...form.register('confirmPassword')}
            />
            <button
              type="button"
              onClick={() => setShowConfirmation((current) => !current)}
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
              aria-label={showConfirmation ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {showConfirmation ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
            </button>
          </div>
          <FieldError errors={[form.formState.errors.confirmPassword]} />
        </Field>
        <FieldError errors={[form.formState.errors.root]} />
        <Field>
          <Button data-testid="set-password-submit" type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
            Définir mon mot de passe
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
